import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../../../connection/api';

const AdminDataContext = createContext();

export const useAdminData = () => {
  const context = useContext(AdminDataContext);
  if (!context) {
    throw new Error('useAdminData must be used within AdminDataProvider');
  }
  return context;
};

export const AdminDataProvider = ({ children }) => {
  // All admin data states
  const [adminData, setAdminData] = useState({
    // Dashboard data
    stats: {
      total: 0,
      allNotVote: 0,
      allVote: 0
    },
    circleStats: [],
    
    // Employee data
    employees: {
      all: [],
      voted: [],
      notVoted: [],
      pagination: {
        current_page: 1,
        last_page: 1,
        total: 0
      }
    },
    
    // Base data
    circles: [],
    bases: [],
    
    // Users/Supervisors data
    users: [],
    
    // Loading states
    isLoading: {
      stats: false,
      employees: false,
      circles: false,
      bases: false,
      users: false
    },
    
    // Last refresh timestamp
    lastRefresh: null
  });

  // Auto-refresh interval (5 minutes = 300000ms)
  const REFRESH_INTERVAL = 5 * 60 * 1000;

  // Get authentication token
  const getToken = () => localStorage.getItem('token');

  // Fetch dashboard statistics
  const fetchStats = useCallback(async () => {
    try {
      setAdminData(prev => ({
        ...prev,
        isLoading: { ...prev.isLoading, stats: true }
      }));

      const token = getToken();
      const response = await api.get("/admin/amar", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      if (response.data.success === true) {
        setAdminData(prev => ({
          ...prev,
          stats: {
            total: response.data.allEmployee,
            allNotVote: response.data.allEmployeeNotVote,
            allVote: response.data.allEmployeeVote
          }
        }));
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setAdminData(prev => ({
        ...prev,
        isLoading: { ...prev.isLoading, stats: false }
      }));
    }
  }, []);

  // Fetch circle statistics
  const fetchCircleStats = useCallback(async () => {
    try {
      const token = getToken();
      const response = await api.get("/admin/getAllVoteStats", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      if (response.data.success === true) {
        const circlesData = response.data.data.map(circle => {
          let circleTotal = 0;
          let circleVoted = 0;
          let circleNotVoted = 0;
          
          Object.values(circle.bases).forEach(base => {
            circleTotal += Number(base.all) || 0;
            circleVoted += Number(base.voted) || 0;
            circleNotVoted += Number(base.not_voted) || 0;
          });
          
          return {
            id: circle.circle_id,
            name: circle.circle_name,
            bases: circle.bases,
            totals: {
              all: circleTotal,
              voted: circleVoted,
              not_voted: circleNotVoted
            }
          };
        });
        
        setAdminData(prev => ({
          ...prev,
          circleStats: circlesData
        }));
      }
    } catch (error) {
      console.error("Error fetching circle stats:", error);
    }
  }, []);

  // Fetch all employees with pagination
  const fetchEmployees = useCallback(async (page = 1, perPage = 10, type = 'all') => {
    try {
      setAdminData(prev => ({
        ...prev,
        isLoading: { ...prev.isLoading, employees: true }
      }));

      const token = getToken();
      let endpoint;
      
      switch (type) {
        case 'voted':
          endpoint = `/getAllVoteStats?page=${page}&per_page=${perPage}`;
          break;
        case 'notVoted':
          endpoint = `/admin/listNoteVote?page=${page}&per_page=${perPage}`;
          break;
        default:
          endpoint = `/admin/employee/index?page=${page}&per_page=${perPage}`;
      }

      const response = await api.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (response.data.success === true) {
        setAdminData(prev => ({
          ...prev,
          employees: {
            ...prev.employees,
            [type]: response.data.data || [],
            pagination: response.data.pagination || prev.employees.pagination
          }
        }));
      }
    } catch (error) {
      console.error(`Error fetching ${type} employees:`, error);
    } finally {
      setAdminData(prev => ({
        ...prev,
        isLoading: { ...prev.isLoading, employees: false }
      }));
    }
  }, []);

  // Fetch employees by base ID
  const fetchEmployeesByBase = useCallback(async (baseId, page = 1, perPage = 10) => {
    try {
      setAdminData(prev => ({
        ...prev,
        isLoading: { ...prev.isLoading, employees: true }
      }));

      const token = getToken();
      const response = await api.get(`admin/listVoteByBase?page=${page}&per_page=${perPage}&baseId=${baseId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (response.data.success === true) {
        setAdminData(prev => ({
          ...prev,
          employees: {
            ...prev.employees,
            all: response.data.data || [],
            pagination: response.data.pagination || prev.employees.pagination
          }
        }));
      }
    } catch (error) {
      console.error("Error fetching employees by base:", error);
    } finally {
      setAdminData(prev => ({
        ...prev,
        isLoading: { ...prev.isLoading, employees: false }
      }));
    }
  }, []);

  // Fetch employees by circle ID
  const fetchEmployeesByCircle = useCallback(async (circleId, page = 1, perPage = 10) => {
    try {
      setAdminData(prev => ({
        ...prev,
        isLoading: { ...prev.isLoading, employees: true }
      }));

      const token = getToken();
      const response = await api.get(`admin/listVoteByCircleId?page=${page}&per_page=${perPage}&circleId=${circleId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (response.data.success === true) {
        setAdminData(prev => ({
          ...prev,
          employees: {
            ...prev.employees,
            all: response.data.data || [],
            pagination: response.data.pagination || prev.employees.pagination
          }
        }));
      }
    } catch (error) {
      console.error("Error fetching employees by circle:", error);
    } finally {
      setAdminData(prev => ({
        ...prev,
        isLoading: { ...prev.isLoading, employees: false }
      }));
    }
  }, []);

  // Fetch circles
  const fetchCircles = useCallback(async () => {
    try {
      setAdminData(prev => ({
        ...prev,
        isLoading: { ...prev.isLoading, circles: true }
      }));

      const token = getToken();
      const response = await api.get("/circle/index", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      setAdminData(prev => ({
        ...prev,
        circles: response.data.AllData || []
      }));
    } catch (error) {
      console.error("Error fetching circles:", error);
    } finally {
      setAdminData(prev => ({
        ...prev,
        isLoading: { ...prev.isLoading, circles: false }
      }));
    }
  }, []);

  // Fetch bases
  const fetchBases = useCallback(async () => {
    try {
      setAdminData(prev => ({
        ...prev,
        isLoading: { ...prev.isLoading, bases: true }
      }));

      const token = getToken();
      const response = await api.get("/base/index", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      setAdminData(prev => ({
        ...prev,
        bases: response.data.data || []
      }));
    } catch (error) {
      console.error("Error fetching bases:", error);
    } finally {
      setAdminData(prev => ({
        ...prev,
        isLoading: { ...prev.isLoading, bases: false }
      }));
    }
  }, []);

  // Fetch users/supervisors
  const fetchUsers = useCallback(async () => {
    try {
      setAdminData(prev => ({
        ...prev,
        isLoading: { ...prev.isLoading, users: true }
      }));

      const token = getToken();
      const response = await api.get("/admin/getListUser", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      setAdminData(prev => ({
        ...prev,
        users: response.data.data || []
      }));
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setAdminData(prev => ({
        ...prev,
        isLoading: { ...prev.isLoading, users: false }
      }));
    }
  }, []);

  // Refresh all data
  const refreshAllData = useCallback(async () => {
    console.log('🔄 Auto-refreshing admin data...');
    
    try {
      // Fetch all data in parallel for better performance
      await Promise.all([
        fetchStats(),
        fetchCircleStats(),
        fetchCircles(),
        fetchBases(),
        fetchUsers()
      ]);

      // Update last refresh timestamp
      setAdminData(prev => ({
        ...prev,
        lastRefresh: new Date()
      }));

      console.log('✅ Admin data refreshed successfully');
    } catch (error) {
      console.error('❌ Error refreshing admin data:', error);
    }
  }, [fetchStats, fetchCircleStats, fetchCircles, fetchBases, fetchUsers]);

  // Initial data load
  useEffect(() => {
    refreshAllData();
  }, [refreshAllData]);

  // Set up auto-refresh interval
  useEffect(() => {
    const interval = setInterval(() => {
      refreshAllData();
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [refreshAllData, REFRESH_INTERVAL]);

  // Context value
  const contextValue = {
    // Data
    ...adminData,
    
    // Actions
    refreshAllData,
    fetchEmployees,
    fetchEmployeesByBase,
    fetchEmployeesByCircle,
    
    // Utils
    refreshInterval: REFRESH_INTERVAL
  };

  return (
    <AdminDataContext.Provider value={contextValue}>
      {children}
    </AdminDataContext.Provider>
  );
};

export default AdminDataContext;
